<template>
    <div class="filter-component" ref="rootElement">
        <h4 class="filter-heading">{{ title }}</h4>
        <div class="dropdown">
            <div class="dropdown-select" @click="handleCheckbox" @keyup.enter="handleCheckbox" tabindex="0">
                <div class="select-values-text-container">
                    <span v-if="!selectedValues.length" class="placeholder-text">{{ placeholder }}</span>
                    <div class="selected-values" v-else>
                        <span class="selected-values__text">{{ selectedValues.join(', ') }}</span>
                        <span class="selected-values__count"> {{ selectedValues.length }}</span>
                    </div>
                </div>
                <div class="dropdown-select__icon icon icon--arrow-down" :class="{ 'dropdown-select__icon--open': showCheckboxes }"></div>
            </div>
            <div class="checkbox-list" v-show="showCheckboxes" ref="dropdownMenu">
                <ClearButton class="checkbox-list__clear" v-if="selectedValues.length" @click="clearFilter" />
                <div class="checkbox" v-for="value in uniqueValues" :key="value">
                    <input type="checkbox" :id="`checkbox-${value}`" :value="value" v-model="selectedValues" />
                    <label :for="`checkbox-${value}`">{{ value }}</label>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted, PropType } from 'vue';
import { useFilterStore } from "../../stores/filterStore";
import { IFilter } from 'interfaces/filter/IFilter';
import ClearButton from '@/components/clear/ClearButton.vue';
import { useSearch } from '@/services/search-service';
import { useFilterState } from '@/services/filter-state-service';
import { useUtil } from '@/services/util-service';

const props = defineProps({
    dataKey: {
        type: [String, Array] as PropType<string | string[]>,
        default: () => []
    },
    placeholder: String,
    title: String
});

const rootElement = ref<HTMLElement | null>(null);

// We always want to handle dataKey as an array
const dataKeyIsArray = Array.isArray(props.dataKey);
const dataKeys = computed(() => dataKeyIsArray ? props.dataKey : [props.dataKey]);

const { getUniqueValues } = useSearch();
const { updateFilter } = useFilterState();
const { arraysContainTheSameValues } = useUtil();

const dropdownMenu = ref<HTMLElement | null>(null);
const filterStore = useFilterStore();
const selectedValues = ref<string[]>([]);
const showCheckboxes = ref(false);
const uniqueValues = computed(() => getUniqueValues(dataKeys.value ?? ''));

onMounted(() => {
    // Initialize selected values from active filters, they've come as query parameters
    selectedValues.value = filterStore.activeFilters
        .filter((f: IFilter) => f.dataKey === props.dataKey || arraysContainTheSameValues(f.dataKey as string[], dataKeys.value)) // remmember they can be both string or string[]
        .flatMap((f: IFilter) =>
            (Array.isArray(f.value) ? f.value : [f.value])
                .map(v => String(v))); // we only want to handle strings here anyway

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true);
    document.removeEventListener('keydown', handleEscape);
});

const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node | null;
    if (target && rootElement.value && !rootElement.value.contains(target)) {
        showCheckboxes.value = false;
    }
};

const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        showCheckboxes.value = false;
    }
};

watch(selectedValues, (newValue, oldValue) => {
    if (newValue === oldValue || arraysContainTheSameValues(newValue, oldValue)) {
        return; // No change in selected values
    }

    const filter: IFilter = {
        dataKey: dataKeyIsArray ? dataKeys.value : props.dataKey,
        value: selectedValues.value
    };
    updateFilter(filter);
});

const handleCheckbox = () => {
    showCheckboxes.value = !showCheckboxes.value;
}

function clearFilter() {
    selectedValues.value = [];
}

// Reset min and max if all filters are removed
watch(filterStore.activeFilters, (newFilters )=> {
  if (newFilters.length === 0) {
    clearFilter();
  }
});

</script>
